import React from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { LegalActsData, CompanyData } from '../../types/inpi';
import { formatDate } from '../../utils/inseeCodeMappings';
import inpiService from '../../services/inpiService';

interface LegalActsCardProps {
  legalActs: LegalActsData | null;
  isLoading: boolean;
  error: string | null;
  onPageChange: (page: number) => void;
  companyData?: CompanyData | null; // Optional company data to check if it's an entrepreneur
}

const LegalActsCard: React.FC<LegalActsCardProps> = ({ 
  legalActs, 
  isLoading, 
  error,
  onPageChange,
  companyData
}) => {
  if (error) {
    return (
      <div>
        <p className="text-sm text-gray-600">Données temporairement indisponibles</p>
      </div>
    );
  }

  const currentPage = legalActs?.pagination?.page || 1;
  const totalPages = legalActs?.pagination?.total_pages || 1;
  const hasActs = legalActs?.items && legalActs.items.length > 0;

  // Loading skeleton for table rows
  const renderTableRowsSkeleton = () => (
    Array.from({ length: 5 }).map((_, index) => (
      <tr key={index}>
        <td className="px-6 py-4">
          <Skeleton width={120} />
        </td>
        <td className="px-6 py-4">
          <Skeleton width={150} />
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <Skeleton width={100} />
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <Skeleton width={80} />
        </td>
      </tr>
    ))
  );

  // Loading skeleton for pagination
  const renderPaginationSkeleton = () => (
    <div className="flex justify-between items-center mt-4">
      <div className="text-sm text-gray-700">
        <Skeleton width={80} />
      </div>
      <div className="inline-flex shadow-sm">
        <Skeleton width={240} height={36} className="rounded" />
      </div>
    </div>
  );

  return (
    <div>
      {isLoading ? (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type d'acte
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Décision
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date de dépôt
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {renderTableRowsSkeleton()}
            </tbody>
          </table>
          {renderPaginationSkeleton()}
        </div>
      ) : (
        <>
          {hasActs ? (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type d'acte
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Décision
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date de dépôt
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {legalActs?.items?.map((act) => (
                      <tr key={act.id}>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {act.typeRdd && act.typeRdd.length > 0 
                            ? act.typeRdd.map(type => type.typeActe).join(', ')
                            : (act.typeDocument || act.type_acte || 'Non renseigné')
                          }
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {act.typeRdd && act.typeRdd.length > 0 
                            ? act.typeRdd.map(type => type.decision).filter(Boolean).join(', ') || 'Non renseigné'
                            : (act.libelle || 'Non renseigné')
                          }
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {act.dateDepot ? formatDate(act.dateDepot) : (act.date_depot ? formatDate(act.date_depot) : 'Non renseigné')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <a 
                            href={inpiService.getDocumentDownloadUrl(act.id, 'acte')}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800"
                          >
                            Télécharger
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-between items-center mt-4">
                  <div className="text-sm text-gray-700">
                    Page {currentPage} sur {totalPages}
                  </div>
                  <div className="inline-flex shadow-sm rounded-md">
                    <button
                      onClick={() => onPageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                        currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      <span className="sr-only">Précédent</span>
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                    
                    {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                      // Logic to show 5 pages max, centered around current page
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => onPageChange(pageNum)}
                          className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium ${
                            currentPage === pageNum ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600' : 'bg-white text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    
                    <button
                      onClick={() => onPageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                        currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      <span className="sr-only">Suivant</span>
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-gray-600">
              {companyData?.formality?.typePersonne === 'P' || companyData?.formality?.content?.personnePhysique
                ? "Les entrepreneurs individuels n'ont généralement pas d'actes juridiques enregistrés."
                : "Aucun acte juridique disponible pour cette entreprise."}
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default LegalActsCard; 